/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveTrendTab,
  selectIsCumulative,
  selectIsTrendDeAggregate,
  selectTabularView,
  selectTrendTimeFilterList,
  selectTrendTimeVisuals,
  selectTrendTimeWeeklyView,
  selectIsFocusOnSet,
  selectTrendHotelFocusOn,
  selectTrendStartDate,
  selectTrendEndDate,
  selectTrendTimeDateComparison,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  selectTrendTargetDate,
  selectEmbedToken,
  selectHotelName,
  selectTabList,
} from 'modules/dashboard/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import TrendFilter from 'modules/dashboard/components/tab-container/trend-tabs/components/filter-panel';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { CustomToggleSwitch } from 'modules/common/components';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import TrendSegmentGraphicalWidget from 'modules/dashboard/components/tab-container/trend-tabs/components/widget-pane/trend-widgets/trend-segment-widget/graphical-visual-view';
import TrendTimeGraphicalWidget from 'modules/dashboard/components/tab-container/trend-tabs/components/widget-pane/trend-widgets/trend-time-widget/graphical-visual-view';
import TrendHotelGraphicalWidget from 'modules/dashboard/components/tab-container/trend-tabs/components/widget-pane/trend-widgets/trend-hotel-widget/graphical-visual-view';
import { differenceInDays, isSameDay } from 'date-fns';
import { compareWindows } from 'modules/common/ui-controller';
import { getWeeklyAlignedCustomDate } from 'modules/common/utils/dateComparisonUtils';
import { formatDate, getVisualName } from 'modules/dashboard/functions';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import { ENVIRONMENT } from 'config';
import TrendTimeSegmentTabularWidget from './trend-time-widget/time-segment-tabular-visual-view';
import TrendTimeSegmentGraphicalWidget from './trend-time-widget/time-segment-graphical-visual-view';
import TrendHotelSegmentTabularWidget from './trend-hotel-widget/hotel-segment-tabular-visual-view';
import TrendHotelSegmentGraphicalWidget from './trend-hotel-widget/hotel-segment-graphical-visual-view';
import { CERULEAN_TREND_GRANULARITY } from '../../../constants';
import MuiTrendTimeTabularWidget from './trend-time-widget/mui-tabular-visual-view';
import MuiTrendHotelTabularWidget from './trend-hotel-widget/mui-tabular-hotel-visual-view';
import MuiTrendSegmentTabularWidget from './trend-segment-widget/mui-tabular-visual-view';
import TrendSegmentTabularWidget from './trend-segment-widget/tabular-visual-view';
import MuiTimeSegmentTabularWidget from './trend-time-widget/mui-tabular-time-segment-visual-view';
/**
 * Trend tab widget implementation
 * @param {Number} value - Trend time widget tab value
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const Widget = ({ value, latestDate, reportType, hotelTimezone, toggleDisabled }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const filterList = useSelector(selectTrendTimeFilterList);
  const tokenDetails = useSelector(selectEmbedToken);
  const visuals = useSelector(selectTrendTimeVisuals);
  const activeTab = useSelector(selectActiveTrendTab);
  const tabularView = useSelector(selectTabularView); // States are obtained from redux store. Refer each selector function for details.
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const startDate = useSelector(selectWeeklyTrendStartDate);
  const endDate = useSelector(selectWeeklyTrendEndDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const isCumulative = useSelector(selectIsCumulative);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const selectedHotel = useSelector(selectHotelName);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const TAB_VALUE = useSelector(selectTabList);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const comparisonStartDate = useSelector(selectTrendTimeStartDate);
  const comparisonEndDate = useSelector(selectTrendTimeEndDate);
  //
  const [isLoaded, setIsLoaded] = useState(false);
  // To get visuals based on the configuration
  const getWidget = (index, cssClassName, displayFunction) => {
    const found = visuals?.find((visual) => visual?.order === index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filterList}
          tab={TAB_VALUE.TREND_TIME}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  //
  const [label, setLabel] = useState(null);
  // Triggered when startDate, endDate, targetDate, weeklyViewToggle, isCumulative values change
  useEffect(() => {
    // Show adjusted stay dates for weekly view toggle on
    if (isCumulative) {
      setLabel(null);
      return;
    }
    const labels = [];
    if (
      weeklyViewToggle &&
      (!isSameDay(new Date(startDate), new Date(targetDate.startDate)) ||
        !isSameDay(new Date(endDate), new Date(targetDate.endDate)))
    ) {
      labels.push(
        `Stay date selection adjusted.  New stay date range from: ${formatDate(
          startDate
        )} to ${formatDate(endDate)}`
      );
    }
    // Show adjusted custom comparison dates for weekly view when they differ from
    // the user's original custom selection
    const isWeeklyAdjusted =
      weeklyViewToggle &&
      !isCumulative &&
      (activeTab === REPORT_TYPE.TREND_TIME ||
        (activeTab === REPORT_TYPE.TREND_SEGMENT && isTrendDeAggregate));
    if (
      dateComparison === compareWindows.cus &&
      isWeeklyAdjusted &&
      comparisonStartDate &&
      comparisonEndDate
    ) {
      const adjustedComparisonDate = getWeeklyAlignedCustomDate(
        { startDate, endDate },
        targetDate?.endDate,
        { startDate: comparisonStartDate, endDate: comparisonEndDate }
      );
      if (
        !isSameDay(new Date(adjustedComparisonDate.startDate), new Date(comparisonStartDate)) ||
        !isSameDay(new Date(adjustedComparisonDate.endDate), new Date(comparisonEndDate))
      ) {
        labels.push(
          `Custom comparison stay date selection adjusted. New custom comparison date range from: ${formatDate(
            adjustedComparisonDate.startDate
          )} to ${formatDate(adjustedComparisonDate.endDate)}`
        );
      }
    }
    setLabel(labels.length > 0 ? labels.join('\n') : null);
  }, [
    startDate,
    endDate,
    targetDate,
    weeklyViewToggle,
    isCumulative,
    dateComparison,
    comparisonStartDate,
    comparisonEndDate,
    activeTab,
    isTrendDeAggregate,
  ]);
  //
  useEffect(() => {
    const days = differenceInDays(new Date(targetDate.endDate), new Date(targetDate.startDate)) + 1;

    let granularity = '';
    if (days > 1460) {
      granularity = CERULEAN_TREND_GRANULARITY.YEARLY;
    } else if (days >= 365) {
      granularity = CERULEAN_TREND_GRANULARITY.MONTHLY;
    } else if (days > 60) {
      granularity = CERULEAN_TREND_GRANULARITY.WEEKLY;
    } else {
      granularity = CERULEAN_TREND_GRANULARITY.DAILY;
    }
    dispatch(trendActions.setTrendGranularity(granularity));
  }, [targetStartDate, targetEndDate]);
  // get trend segment widget based on the environment variable
  const trendSegmentWidget =
    ENVIRONMENT?.ENABLE_TREND_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <MuiTrendSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <TrendSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
    );
  // get time segment widget based on the environment variable
  const timeSegmentWidget =
    ENVIRONMENT?.ENABLE_TIME_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <MuiTimeSegmentTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <TrendTimeSegmentTabularWidget setIsTimeLoaded={setIsLoaded} />
    );
  // get hotel segment widget based on the environment variable
  const segmentHotelWidget =
    ENVIRONMENT?.ENABLE_HOTEL_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <MuiTimeSegmentTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <TrendHotelSegmentTabularWidget setIsTimeLoaded={setIsLoaded} />
    );
  //
  return (
    <>
      <Grid
        item
        xs={2}
        md={2}
        cc={2.5}
        sx={{
          ml: {
            xs: 0,
            cc: 0.25,
            md: 0,
          },
        }}
      >
        <TrendFilter
          reportType={reportType}
          visual={getWidget(
            100,
            styles.symmetricComparisonWindow,
            trendActions.setSymmetricComparisonIsDisplay
          )}
          visualName={getVisualName(visuals, 100)}
          tabValue={value}
          toggleDisabled={toggleDisabled}
          latestDate={latestDate}
          hotelTimezone={hotelTimezone}
          isLoaded={isLoaded}
        />
      </Grid>
      {IsFocusOnSet && (
        <Grid container item xs={10} cc={9.25} md={10} direction="column">
          <Grid item container justifyContent="flex-end" xs={12}>
            <CustomToggleSwitch
              onChange={(event) => dispatch(trendActions.setTabularView(event.target.checked))}
              label="Tabular View"
              checked={tabularView}
              paddingBottom={1}
              className="tabular-view-toggle"
            />
          </Grid>
          <Grid item container justifyContent="space-around">
            {label && (
              <Typography
                paddingBottom={1}
                className={styles.weeklyLabel}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {label}
              </Typography>
            )}
          </Grid>
          {activeTab === REPORT_TYPE.TREND_TIME &&
            !isTrendDeAggregate &&
            (tabularView ? (
              <MuiTrendTimeTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
            ) : (
              <TrendTimeGraphicalWidget setIsTimeLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.TREND_SEGMENT &&
            isTrendDeAggregate &&
            hotelFocusOn?.id !== selectedHotel?.id &&
            (tabularView ? (
              segmentHotelWidget
            ) : (
              <TrendHotelSegmentGraphicalWidget setIsSegmentLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.TREND_SEGMENT &&
            isTrendDeAggregate &&
            hotelFocusOn?.id === selectedHotel?.id &&
            (tabularView ? (
              timeSegmentWidget
            ) : (
              <TrendTimeSegmentGraphicalWidget setIsTimeLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.TREND_SEGMENT &&
            !isTrendDeAggregate &&
            (tabularView ? (
              trendSegmentWidget
            ) : (
              <TrendSegmentGraphicalWidget setIsSegmentLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.TREND_HOTEL &&
            (tabularView ? (
              <MuiTrendHotelTabularWidget setIsHotelLoaded={setIsLoaded} isLoaded={isLoaded} />
            ) : (
              <TrendHotelGraphicalWidget setIsHotelLoaded={setIsLoaded} />
            ))}
        </Grid>
      )}
    </>
  );
};
//
export default Widget;
